import { Star } from "lucide-react";

/**
 * Display-only star rating. Renders 5 stars in a row with the filled
 * portion representing the given ``value`` (0–5, one-decimal precision).
 *
 * Deliberately compact — used in film cards, dashboard rows, and the
 * film-detail header. For the interactive picker used inside the rating
 * dialog see ``RatingPicker`` below.
 */
export function StarRatingDisplay({ value = 0, size = 16, className = "", showValue = false, count = null }) {
  const rounded = Math.round((Number(value) || 0) * 2) / 2; // nearest half
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid="star-rating-display">
      <span className="inline-flex items-center">
        {[1, 2, 3, 4, 5].map((i) => {
          const filled = rounded >= i;
          const half = !filled && rounded >= i - 0.5;
          return (
            <span key={i} className="relative inline-flex" style={{ width: size, height: size }}>
              <Star
                className="text-white/15"
                style={{ width: size, height: size }}
                fill="currentColor"
              />
              {(filled || half) && (
                <span className="absolute inset-0 overflow-hidden" style={{ width: half ? size / 2 : size }}>
                  <Star
                    className="text-gold"
                    style={{ width: size, height: size }}
                    fill="currentColor"
                  />
                </span>
              )}
            </span>
          );
        })}
      </span>
      {showValue && (
        <span className="text-xs text-foreground/70 tabular-nums" data-testid="star-rating-value">
          {rounded > 0 ? rounded.toFixed(1) : "—"}
          {count !== null && <span className="text-foreground/40 ml-1">({count})</span>}
        </span>
      )}
    </span>
  );
}

/**
 * Interactive 5-star picker used inside the post-watch rating dialog.
 * Emits an integer 1..5 via ``onChange``. Hover previews the selection
 * so tapping feels tactile on mobile too (touch triggers hover on
 * click-in).
 */
export function RatingPicker({ value = 0, onChange, size = 32, disabled = false }) {
  return (
    <div className="inline-flex items-center gap-1" role="radiogroup" aria-label="Rate this film" data-testid="rating-picker">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = value >= i;
        return (
          <button
            key={i}
            type="button"
            onClick={() => !disabled && onChange?.(i)}
            disabled={disabled}
            role="radio"
            aria-checked={value === i}
            aria-label={`${i} star${i === 1 ? "" : "s"}`}
            className={`p-1 rounded transition-transform ${
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:scale-110 active:scale-95"
            }`}
            data-testid={`rating-star-${i}`}
          >
            <Star
              className={filled ? "text-gold" : "text-white/25"}
              style={{ width: size, height: size }}
              fill="currentColor"
            />
          </button>
        );
      })}
    </div>
  );
}
